<template>
  <div class="chart-container">
    <b-card>
      <b-card-header>
        <b-card-title>
          Product Sales Analysis Over Time
        </b-card-title>
      </b-card-header>

      <b-row class="mb-3">
        <b-col>
          <label for="productFilter">Select Products (max 5):</label>
          <b-form-select
            id="productFilter"
            v-model="selectedProducts"
            :options="productOptions"
            multiple
            :select-size="4"
            @change="updateChart"
          ></b-form-select>
        </b-col>
      </b-row>

      <apexcharts
        type="line"
        height="500"
        :options="chartOptions"
        :series="chartSeries"
      ></apexcharts>
    </b-card>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import {
  BCard,
  BCardTitle,
  BCardHeader,
  BRow,
  BCol,
  BFormSelect,
} from "bootstrap-vue";

export default {
  name: "ProductSalesChart",
  components: {
    apexcharts: VueApexCharts,
    BCard,
    BCardTitle,
    BCardHeader,
    BRow,
    BCol,
    BFormSelect,
  },
  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      selectedProducts: [],
    };
  },
  computed: {
    productOptions() {
      return this.uniqueProducts.map(product => ({
        value: product,
        text: product
      }));
    },

    sortedDates() {
      const dates = [...new Set(this.data.map((item) => item.period))];
      return dates.sort();
    },
    
    uniqueProducts() {
      return [...new Set(this.data.map((item) => item.productName))];
    },

    filteredData() {
      let filtered = this.data;
      
      if (this.selectedProducts.length > 0) {
        filtered = filtered.filter(item => 
          this.selectedProducts.includes(item.productName)
        );
      }

      return filtered;
    },
  
    chartSeries() {
      const series = [];
      const productsToShow = this.selectedProducts.length > 0 
        ? this.selectedProducts 
        : this.uniqueProducts.slice(0, 5);

      productsToShow.forEach((productName) => {
        const quantityData = this.sortedDates.map((period) => {
          const periodData = this.filteredData.find(
            (item) => item.productName === productName && item.period === period
          );
          return periodData ? periodData.totalQuantitySold : null;
        });
        series.push({
          name: `${productName} - Quantity`,
          type: "line",
          data: quantityData,
        });

        const revenueData = this.sortedDates.map((period) => {
          const periodData = this.filteredData.find(
            (item) => item.productName === productName && item.period === period
          );
          return periodData ? periodData.totalValueSold : null;
        });
        series.push({
          name: `${productName} - Revenue`,
          type: "line",
          data: revenueData,
        });
      });
      return series;
    },

    chartOptions() {
      return {
        chart: {
          id: "product-sales-analysis",
          type: "line",
          stacked: false,
          animations: {
            enabled: false
          },
          zoom: {
            enabled: true
          }
        },
        xaxis: {
          categories: this.sortedDates,
          title: { text: "Period" },
          labels: {
            rotate: -45,
            trim: true
          }
        },
        yaxis: [
          {
            title: { 
              text: "Quantity Sold",
              style: {
                fontSize: '12px',
              }
            },
            decimalsInFloat: 0,
          },
          {
            opposite: true,
            title: { 
              text: "Revenue (Rs)",
              style: {
                fontSize: '12px',
              }
            },
            decimalsInFloat: 2,
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function(value, { seriesIndex }) {
              if (value === null) return "No data";
              return seriesIndex % 2 === 0 
                ? Math.round(value) + " units"
                : "Rs" + value.toFixed(2);
            }
          }
        },
        legend: {
          position: "bottom",
          horizontalAlign: "center",
          offsetY: 8,
          fontSize: '12px',
          itemMargin: {
            horizontal: 10,
            vertical: 5
          }
        },
        stroke: {
          width: 2,
          curve: 'smooth'
        },
        colors: ["#008FFB", "#00E396", "#FEB019", "#FF4560", "#775DD0", 
                "#546E7A", "#26a69a", "#D10CE8", "#2E294E", "#F46036"],
        markers: {
          size: 4,
          hover: {
            size: 6
          }
        },
        grid: {
          xaxis: {
            lines: {
              show: true
            }
          }
        }
      };
    },
  },
  methods: {
    updateChart() {
    },
  },
  mounted() {
    if (this.uniqueProducts.length > 0) {
      this.selectedProducts = this.uniqueProducts.slice(0, 5);
    }
  },
};
</script>

<style scoped>
.chart-container {
  padding: 1rem;
}
</style>